import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authApi } from "../api/endpoints";
import { ApiError } from "../api/client";
import { InfoBanner } from "../components/Feedback";
import { Icon } from "../components/Icon";

type Status = "verifying" | "success" | "error";

export function VerifyPage() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [status, setStatus] = useState<Status>("verifying");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [resendInfo, setResendInfo] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const verifyToken = async () => {
      if (!token) {
        setStatus("error");
        setMessage("No verification token was provided.");
        return;
      }
      try {
        const res = await authApi.verify(token);
        if (!active) return;
        setStatus("success");
        setMessage(res.message);
      } catch (err) {
        if (!active) return;
        setStatus("error");
        setMessage(err instanceof ApiError ? err.message : "Verification failed.");
      }
    };
    verifyToken();
    return () => {
      active = false;
    };
  }, [token]);

  const resend = async () => {
    setResendInfo(null);
    try {
      const res = await authApi.resend(email);
      setResendInfo(res.message);
    } catch (err) {
      setResendInfo(err instanceof ApiError ? err.message : "Could not resend verification.");
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-brand">
        <span className="brand-mark">
          <Icon name="layers" size={22} />
        </span>
        Ticket Tracker
      </div>

      <div className="auth-card">
        <h1 style={{ textAlign: "center" }}>Email verification</h1>

        {status === "verifying" && (
          <div className="verify-body">
            <div className="verify-icon busy">
              <span className="spinner" style={{ width: 30, height: 30 }} />
            </div>
            <p className="muted">Verifying your email…</p>
          </div>
        )}

        {status === "success" && (
          <>
            <div className="verify-icon ok">
              <Icon name="check" size={44} />
            </div>
            <div className="verify-body">
              <h2>Email verified</h2>
              <p className="muted">{message || "Your account is ready to use."}</p>
            </div>
            <Link className="btn btn-primary btn-block" to="/login">
              Continue to login
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="verify-icon bad">
              <Icon name="alert" size={40} />
            </div>
            <div className="verify-body">
              <h2>Expired or invalid link</h2>
              <p className="muted">{message}</p>
            </div>
            <label className="field">
              <span className="field-label">Get a fresh verification link</span>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <button type="button" className="secondary btn-block" onClick={resend} disabled={!email}>
              Resend verification email
            </button>
            {resendInfo && <InfoBanner message={resendInfo} />}
            <p className="auth-alt">
              <Link to="/login">Back to login →</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
